import api from "@/configs/axios-config";

export type exchangeRateMaster = {
  exchange_rate_supplier_id: number;
  exchange_rate_supplier_name: string;
  currency_id: number;
  currency_name: string;
  exchange_rate: number;
  rounding: "ROUND" | "ROUND_UP" | "ROUND_DOWN";
  rounding_digit: number;
};

export type exchangeRatePart = {
  part_id: number;
  part_number: string;
  part_name: string;
  ac: string;
  rt: string;
  currency_code: string;
  exchange_rate_supplier_id: number;
  currency_id: number;
  exchange_rate: number;
  price: number;
  part_price: number;
  oem_price: number;
  spare_price: number;
  is_active: boolean;
  is_flagged: boolean;
  is_admin_flagged: boolean;
  total_oem_other_process_cost: number;
  total_spare_other_process_cost: number;
  oem_other_process: {
    id: number;
    cost: number;
  }[];
  spare_other_process: {
    id: number;
    cost: number;
  }[];
};

export type breakdownProcess = {
  id: number;
  name: string;
  ms_process_id?: number;
};

export type exchangeRateProcess = {
  oem_other_process: breakdownProcess[];
  spare_other_process: breakdownProcess[];
};

export type exchangeRateBody = {
  part_id: number;
  exchange_rate_supplier_id: number;
  currency_id: number;
  currency_code: string;
  price: number;
  is_active: boolean;
  oem_other_process: {
    id: number;
    cost: number;
  }[];
  spare_other_process: {
    id: number;
    cost: number;
  }[];
};

/** ใช้เฉพาะ bulk import — other-process key ด้วยชื่อแทน id เพราะชื่อ process มาจาก column
 * header ของไฟล์ Excel ระบบเก่า และอาจยังไม่มีในระบบ (backend auto-create ให้) */
export type exchangeRateBulkImportItem = {
  part_id: number;
  exchange_rate_supplier_id: number;
  currency_id: number;
  currency_code: string;
  price: number;
  is_active: boolean;
  oem_other_process: {
    name: string;
    cost: number;
  }[];
  spare_other_process: {
    name: string;
    cost: number;
  }[];
};

export type exchangeRateProcessBody = {
  id?: number;
  ms_process_id: number;
  supplier_code: string;
};

export type CurrencyOption = {
  label: string;
  value: {
    exchange_rate_supplier_id: number;
    currency_id: number;
    exchange_rate: number;
    rounding: "ROUND" | "ROUND UP" | "ROUND DOWN";
    rounding_digit: number;
  };
};

export type exchangeRatePartHistory = {
  part_id: number;
  year: number;
  quarter: number;
  part_price: number;
  is_active: boolean;
};

export const getExchangeRateMaster = async (): Promise<
  exchangeRateMaster[]
> => {
  const response = await api.get("/exchange-rate-breakdown/master");
  return response.data;
};

export const getExchangeRateParts = async (
  supplierCode: string,
  isActive: boolean,
): Promise<exchangeRatePart[]> => {
  const response = await api.get(`/exchange-rate-breakdown/${supplierCode}`, {
    params: { is_active: isActive },
  });
  return response.data;
};

export const createExchangeRatePart = async (
  supplierCode: string,
  data: exchangeRateBody,
) => {
  const response = await api.post(
    `/exchange-rate-breakdown/${supplierCode}`,
    data,
  );
  return response.data;
};

export type exchangeRateBulkImportResponse = {
  inserted_count: number;
  inserted_part_ids: number[];
};

export const bulkImportExchangeRateParts = async (
  supplierCode: string,
  data: exchangeRateBulkImportItem[],
): Promise<exchangeRateBulkImportResponse> => {
  const response = await api.post(
    `/exchange-rate-breakdown/${supplierCode}/import`,
    data,
  );
  return response.data;
};

export const updateExchangeRatePart = async (
  supplierCode: string,
  data: exchangeRateBody[],
) => {
  const response = await api.put(
    `/exchange-rate-breakdown/${supplierCode}`,
    data,
  );
  return response.data;
};

export const getExchangeRatePartHistory = async (
  partId: number,
): Promise<exchangeRatePartHistory[]> => {
  const response = await api.get(`/exchange-rate-breakdown/history/${partId}`);
  return response.data;
};

export const getExchangeRateProcess = async (
  supplierCode: string,
): Promise<exchangeRateProcess> => {
  const response = await api.get(
    `/exchange-rate-breakdown/process/${supplierCode}`,
  );
  return response.data;
};

export const createExchangeRateOEMOtherProcess = async (
  params: exchangeRateProcessBody,
) => {
  const response = await api.post("/exchange-rate-breakdown/process/oem", {
    supplier_code: params.supplier_code,
    ms_process_id: params.ms_process_id,
  });
  return response.data;
};

export const createExchangeRateSpareOtherProcess = async (
  params: exchangeRateProcessBody,
) => {
  const response = await api.post("/exchange-rate-breakdown/process/spare", {
    supplier_code: params.supplier_code,
    ms_process_id: params.ms_process_id,
  });
  return response.data;
};

export const updateExchangeRateOEMOtherProcess = async (
  params: exchangeRateProcessBody,
) => {
  const response = await api.post(
    "/exchange-rate-breakdown/process/oem/update",
    {
      id: params.id,
      supplier_code: params.supplier_code,
      ms_process_id: params.ms_process_id,
    },
  );
  return response.data;
};

export const updateExchangeRateSpareOtherProcess = async (
  params: exchangeRateProcessBody,
) => {
  const response = await api.put(
    "/exchange-rate-breakdown/process/spare/update",
    {
      id: params.id,
      supplier_code: params.supplier_code,
      ms_process_id: params.ms_process_id,
    },
  );
  return response.data;
};

export const deleteExchangeRateOEMOtherProcess = async (id: number) => {
  const response = await api.delete(
    `/api/v1/exchange-rate-breakdown/process/oem/${id}`,
  );
  return response.data;
};

export const deleteExchangeRateSpareOtherProcess = async (id: number) => {
  const response = await api.delete(
    `/api/v1/exchange-rate-breakdown/process/spare/${id}`,
  );
  return response.data;
};
